<template>
  <Teleport to="body" :disabled="!teleport">
    <aside
      v-if="!fechadaLocal"
      class="barra-aviso-usuario-suporte"
      :class="classeAmbiente"
      role="banner"
      :aria-label="textoBarra"
    >
      <div class="barra-aviso-usuario-suporte__conteudo">
        <span
          class="barra-aviso-usuario-suporte__texto"
          data-testid="barra-aviso-texto"
          :title="textoBarra"
        >
          {{ textoBarra }}
        </span>
      </div>
      <button
        type="button"
        class="barra-aviso-usuario-suporte__botao-fechar"
        data-testid="barra-aviso-fechar"
        :aria-label="labelBotaoFechar"
        :title="labelBotaoFechar"
        @click="fechar"
      >
        <svg
          class="barra-aviso-usuario-suporte__icone-fechar"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    </aside>
  </Teleport>
</template>

<script>
import {
  obterEstadoFechadoNoStorage,
  salvarEstadoFechadoNoStorage
} from './logicaBarraAvisoUsuarioSuporte';

export default {
  name: 'BarraAvisoUsuarioSuporte',
  props: {
    ambiente: {
      type: String,
      required: true,
    },
    empresa: {
      type: String,
      required: true,
    },
    teleport: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['fechar'],
  data() {
    return {
      fechadaLocal: obterEstadoFechadoNoStorage(),
    };
  },
  computed: {
    textoBarra() {
      return `${this.ambiente} - ${this.empresa}`.toUpperCase();
    },
    classeAmbiente() {
      const sufixoLimpo = String(this.ambiente)
        .toLowerCase()
        .replace(/[^a-z0-9_-]/g, '-');
      return `barra-aviso-usuario-suporte--${sufixoLimpo}`;
    },
    labelBotaoFechar() {
      try {
        return this.$t('comuns.fechar');
      } catch {
        return 'Fechar';
      }
    },
  },
  methods: {
    fechar() {
      this.fechadaLocal = true;
      salvarEstadoFechadoNoStorage();
      this.$emit('fechar');
    },
  },
};
</script>

<style lang="scss" scoped>
.barra-aviso-usuario-suporte {
  --barra-aviso-cor-background: var(--cor-info-100);
  --barra-aviso-cor-conteudo: var(--cor-info-contraste);
  --barra-aviso-cor-interacao: var(--cor-info-200);

  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  width: 100vw;
  height: var(--espacamento-36);
  min-height: var(--espacamento-36);
  max-height: var(--espacamento-36);
  z-index: 1000000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--espacamento-0) var(--espacamento-48) var(--espacamento-0) var(--espacamento-16);
  box-sizing: border-box;
  font-family: var(--texto-tipografia);
  font-size: var(--texto-legenda-p-tamanho);
  font-weight: var(--texto-legenda-p-peso);
  line-height: var(--texto-legenda-p-entrelinha);
  color: var(--barra-aviso-cor-conteudo);
  background-color: var(--barra-aviso-cor-background);
  user-select: none;
  overflow: hidden;
  white-space: nowrap;

  &--producao,
  &--proxy-producao {
    --barra-aviso-cor-background: var(--cor-erro-500);
    --barra-aviso-cor-conteudo: var(--cor-erro-contraste);
    --barra-aviso-cor-interacao: var(--cor-erro-300);
  }

  &--homologacao,
  &--homolog {
    --barra-aviso-cor-background: var(--cor-alerta-100);
    --barra-aviso-cor-conteudo: var(--cor-alerta-contraste);
    --barra-aviso-cor-interacao: var(--cor-alerta-200);
  }

  &--desenvolvimento {
    --barra-aviso-cor-background: var(--cor-sucesso-100);
    --barra-aviso-cor-conteudo: var(--cor-sucesso-contraste);
    --barra-aviso-cor-interacao: var(--cor-sucesso-200);
  }

  &[class*="--alpha"] {
    --barra-aviso-cor-background: var(--cor-info-100);
    --barra-aviso-cor-conteudo: var(--cor-info-contraste);
    --barra-aviso-cor-interacao: var(--cor-info-200);
  }

  &__conteudo {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    max-width: 100%;
    min-width: 0;
    overflow: hidden;
    white-space: nowrap;
  }

  &__texto {
    display: inline-block;
    max-width: 100%;
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    text-align: center;
    text-transform: uppercase;
  }

  &__botao-fechar {
    position: absolute;
    right: var(--espacamento-8);
    top: 50%;
    transform: translateY(-50%);
    display: flex;
    align-items: center;
    justify-content: center;
    width: var(--espacamento-28);
    height: var(--espacamento-28);
    padding: var(--espacamento-0);
    margin: var(--espacamento-0);
    background: transparent;
    border: none;
    border-radius: var(--arredondamento-4);
    color: inherit;
    cursor: pointer;
    flex-shrink: 0;
    transition: background-color 0.15s ease;

    &:hover {
      background-color: var(--barra-aviso-cor-interacao);
    }

    &:focus-visible {
      background-color: var(--barra-aviso-cor-interacao);
      outline: var(--espessura-20) solid currentColor;
      outline-offset: calc(var(--espessura-20) * -1);
    }

    &:focus:not(:focus-visible) {
      outline: none;
    }
  }

  &__icone-fechar {
    display: block;
    width: var(--espacamento-16);
    height: var(--espacamento-16);
    stroke-width: var(--espessura-25);
  }
}
</style>
